import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, Package, Calendar, Users, ShoppingBag, X, Command, Sparkles, ArrowRight } from 'lucide-react';
import { searchApi, assistantApi, type UniversalSearchResult, type AssistantChatResponse } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

type EntityType = 'product' | 'event' | 'customer' | 'order';

interface UniversalSearchProps {
  types?: EntityType[];
  placeholder?: string;
  onSelect?: (result: UniversalSearchResult) => void;
  className?: string;
  compact?: boolean;
  autoFocus?: boolean;
}

const TYPE_ICONS: Record<EntityType, React.ElementType> = {
  product: Package,
  event: Calendar,
  customer: Users,
  order: ShoppingBag,
};

const TYPE_COLORS: Record<EntityType, string> = {
  product: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  event: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  customer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  order: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

const TYPE_LABELS: Record<EntityType, string> = {
  product: 'Product',
  event: 'Event',
  customer: 'Client',
  order: 'Order',
};

const ALL_TYPES: EntityType[] = ['product', 'event', 'customer', 'order'];

export function UniversalSearch({
  types,
  placeholder,
  onSelect,
  className = '',
  compact = false,
  autoFocus = false,
}: UniversalSearchProps) {
  const { getToken } = useAuth();
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UniversalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<EntityType | 'all'>('all');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeTypes, setActiveTypes] = useState<Set<EntityType>>(new Set(types || ALL_TYPES));
  const [aiMode, setAiMode] = useState(false);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const effectiveTypes = useMemo(() => types || ALL_TYPES, [types]);
  const activeSearchTypes = useMemo(() => {
    return types || Array.from(activeTypes);
  }, [activeTypes, types]);

  const filteredResults = activeFilter === 'all'
    ? results
    : results.filter((result) => result.entity_type === activeFilter);

  const isNaturalLanguageQuery = useCallback((value: string): boolean => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed.length < 10) return false;

    const words = trimmed.split(/\s+/);
    if (words.length < 4) return false;

    const indicators = [
      /^(show|find|get|list|what|who|how|when|where|which|tell|give|look)/,
      /\?$/,
      /\b(last week|this month|today|yesterday|over \$|under \$|more than|less than|between)\b/,
      /\b(all|every|any|most|recent|latest|oldest|top|best)\b.*\b(orders?|clients?|customers?|products?|events?|bookings?)\b/,
    ];

    return indicators.some((pattern) => pattern.test(trimmed));
  }, []);

  const askAssistant = useCallback(async (message: string) => {
    setAiMode(true);
    setAiLoading(true);
    setLoading(false);
    setResults([]);

    try {
      const response: AssistantChatResponse = await assistantApi.chat(
        { message, surface: 'platform' },
        getToken,
      );
      setAiResponse(response.message);
    } catch (error) {
      console.error('AI search error:', error);
      setAiResponse('Sorry, I could not process that request. Try a simpler search.');
    } finally {
      setAiLoading(false);
    }
  }, [getToken]);

  const performSearch = useCallback(async (searchQuery: string) => {
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setResults([]);
      setAiResponse(null);
      setAiMode(false);
      setLoading(false);
      return;
    }

    if (isNaturalLanguageQuery(trimmedQuery)) {
      await askAssistant(trimmedQuery);
      return;
    }

    setAiMode(false);
    setAiResponse(null);
    setLoading(true);

    try {
      const response = await searchApi.search(
        { q: trimmedQuery, types: activeSearchTypes, limit: 20 },
        getToken,
      );
      setResults(response.results);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [activeSearchTypes, askAssistant, getToken, isNaturalLanguageQuery]);

  useEffect(() => {
    setActiveTypes(new Set(types || ALL_TYPES));
  }, [types]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults([]);
      setAiResponse(null);
      setAiMode(false);
      setActiveIndex(-1);
      return;
    }

    const delay = isNaturalLanguageQuery(query) ? 600 : 250;
    debounceRef.current = setTimeout(() => performSearch(query), delay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isNaturalLanguageQuery, performSearch, query]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [activeFilter, results]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const getEntityPath = (result: UniversalSearchResult): string => {
    const base = tenantId ? `/${tenantId}` : '';

    switch (result.entity_type) {
      case 'product':
        return `${base}/products`;
      case 'event':
        return `${base}/events`;
      case 'customer':
        return `${base}/customers/${result.entity_id}`;
      case 'order':
        return `${base}/orders/${result.entity_id}`;
      default:
        return base;
    }
  };

  const handleSelect = (result: UniversalSearchResult) => {
    const selectedQuery = query;
    setOpen(false);
    setQuery('');
    setResults([]);
    setAiResponse(null);
    setAiMode(false);

    void searchApi.logClick(
      { query: selectedQuery, entityType: result.entity_type, entityId: result.entity_id },
      getToken,
    );

    if (onSelect) {
      onSelect(result);
      return;
    }

    navigate(getEntityPath(result));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filteredResults.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, -1));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0 && filteredResults[activeIndex]) {
      event.preventDefault();
      handleSelect(filteredResults[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const defaultPlaceholder = types
    ? `Search ${types.map((type) => TYPE_LABELS[type].toLowerCase() + 's').join(', ')}...`
    : 'Search everything...';

  const typeCounts = results.reduce<Record<string, number>>((acc, result) => {
    acc[result.entity_type] = (acc[result.entity_type] || 0) + 1;
    return acc;
  }, {});

  const showFilters = !compact && results.length > 0 && effectiveTypes.length > 1;

  const toggleType = (type: EntityType) => {
    setActiveTypes((previous) => {
      const next = new Set(previous);

      if (next.has(type)) {
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }

      return next;
    });
    setActiveFilter('all');
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setAiResponse(null);
    setAiMode(false);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative flex items-center w-full">
        <Search
          size={compact ? 14 : 16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => {
            if (query.trim() || results.length > 0 || aiMode) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || defaultPlaceholder}
          className={`w-full rounded-lg border border-slate-200 bg-white/80 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:border-rose-300 transition-all dark:bg-slate-800/80 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 ${
            compact ? 'pl-7 pr-7 py-1 text-xs' : 'pl-9 pr-20 py-2 text-sm'
          }`}
          aria-label="Universal search"
          aria-autocomplete="list"
          aria-expanded={open}
          data-search-input
        />
        {!compact && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
            {query ? null : (
              <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] text-slate-400 bg-slate-100 dark:bg-slate-700 dark:text-slate-500 rounded px-1.5 py-0.5 font-mono">
                <Command size={9} />K
              </kbd>
            )}
          </div>
        )}
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Clear search"
          >
            <X size={compact ? 12 : 14} />
          </button>
        )}
      </div>

      {!compact && !types && (
        <div className="flex items-center gap-1.5 px-1 py-2 overflow-x-auto">
          {ALL_TYPES.map((type) => {
            const Icon = TYPE_ICONS[type];
            const isActive = activeTypes.has(type);

            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? TYPE_COLORS[type]
                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:hover:bg-slate-700'
                }`}
              >
                <Icon size={12} />
                {TYPE_LABELS[type]}s
              </button>
            );
          })}
        </div>
      )}

      {open && (query.trim() || results.length > 0 || aiMode) && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xl z-50 overflow-hidden">
          {showFilters && !aiMode && (
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 dark:border-slate-800 overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveFilter('all')}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  activeFilter === 'all'
                    ? 'bg-slate-800 text-white dark:bg-white dark:text-slate-900'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
              >
                All {results.length > 0 && <span className="ml-1 opacity-70">{results.length}</span>}
              </button>
              {effectiveTypes.map((type) => {
                const count = typeCounts[type] || 0;
                if (count === 0) return null;

                const Icon = TYPE_ICONS[type];

                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setActiveFilter(activeFilter === type ? 'all' : type)}
                    className={`shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                      activeFilter === type
                        ? 'bg-slate-800 text-white dark:bg-white dark:text-slate-900'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                    }`}
                  >
                    <Icon size={11} />
                    {TYPE_LABELS[type]}s
                    <span className="opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {aiMode ? (
            <div className="p-4">
              {aiLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Sparkles size={14} className="text-rose-400 animate-pulse" />
                  <span>Thinking...</span>
                </div>
              ) : aiResponse ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-rose-500">
                    <Sparkles size={12} />
                    <span>AI Answer</span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setAiMode(false);
                      setAiResponse(null);
                      void performSearch(query);
                    }}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                  >
                    <ArrowRight size={10} />
                    Search as keyword instead
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className="max-h-72 overflow-y-auto overscroll-contain" role="listbox">
                {loading && filteredResults.length === 0 && (
                  <div className="px-4 py-4 text-xs text-slate-500 text-center flex items-center justify-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-rose-300 border-t-transparent rounded-full animate-spin" />
                    Searching...
                  </div>
                )}
                {!loading && filteredResults.length === 0 && query.trim() && (
                  <div className="px-4 py-4 text-xs text-slate-500 text-center">
                    <div>
                      No results for <span className="font-medium">"{query}"</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => void askAssistant(query)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-rose-500 hover:text-rose-600 font-medium transition-colors"
                    >
                      <Sparkles size={12} />
                      Ask AI assistant instead
                    </button>
                    {activeFilter !== 'all' && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setActiveFilter('all')}
                          className="underline hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          Show all types
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {filteredResults.map((result, index) => {
                  const Icon = TYPE_ICONS[result.entity_type as EntityType];
                  const isActive = index === activeIndex;

                  return (
                    <button
                      key={`${result.entity_type}-${result.entity_id}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full px-3 py-2.5 text-left flex items-center gap-3 border-b border-slate-50 dark:border-slate-800/60 last:border-0 transition-colors ${
                        isActive ? 'bg-slate-50 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                      }`}
                    >
                      <div className={`p-1.5 rounded-lg shrink-0 ${TYPE_COLORS[result.entity_type as EntityType]}`}>
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                          {result.title}
                        </div>
                        {result.subtitle?.trim() && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {result.subtitle.trim()}
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">
                        {TYPE_LABELS[result.entity_type as EntityType]}
                      </span>
                    </button>
                  );
                })}
              </div>

              {filteredResults.length > 0 && (
                <div className="px-3 py-1.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-400">
                  <span>↑↓ navigate · Enter select · Esc close</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
